import React from 'react';

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQStructuredDataProps {
    items: FAQItem[];
}

const FAQStructuredData: React.FC<FAQStructuredDataProps> = ({ items }) => {
    if (!items || items.length === 0) return null;

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": items.map(item => ({
            "@type": "Question",
            "name": item.question,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": item.answer
            }
        }))
    };

    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        />
    );
};

/**
 * Extract FAQ-like Q&A pairs from HTML content.
 * Looks for patterns like <h3>Question?</h3> followed by <p>Answer</p>
 */
export const extractFAQFromContent = (htmlContent: string): FAQItem[] => {
    const faqItems: FAQItem[] = [];

    // Split the content into sections by headings
    const sections = htmlContent.split(/<(h[23])[^>]*>/gi);
    
    // The first element is content before the first heading
    for (let i = 1; i < sections.length; i += 2) {
        const tagName = sections[i];
        const contentAfterTag = sections[i + 1] || '';
        
        // Find where the heading ends
        const closingTag = `</${tagName}>`;
        const closingIndex = contentAfterTag.toLowerCase().indexOf(closingTag.toLowerCase());
        
        if (closingIndex !== -1) {
            const question = contentAfterTag.substring(0, closingIndex).replace(/<[^>]*>/g, '').trim();
            const body = contentAfterTag.substring(closingIndex + closingTag.length).trim();
            
            // If the heading is a question
            if (question.endsWith('?')) {
                // Extract text from the body, stopping at the next section (already handled by split)
                const answer = body.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
                
                if (question.length > 5 && answer.length > 15) {
                    // Avoid duplicates
                    if (!faqItems.find(item => item.question === question)) {
                        faqItems.push({ question, answer });
                    }
                }
            }
        }
    }

    // Fallback to the original regex if no items found (safety net)
    if (faqItems.length === 0) {
        const regex = /<h[23][^>]*>(.*?\?)<\/h[23]>\s*<p[^>]*>(.*?)<\/p>/gi;
        let match;
        while ((match = regex.exec(htmlContent)) !== null) {
            const question = match[1].replace(/<[^>]*>/g, '').trim();
            const answer = match[2].replace(/<[^>]*>/g, '').trim();
            if (question.length > 5 && answer.length > 15) {
                faqItems.push({ question, answer });
            }
        }
    }

    return faqItems;
};

export default FAQStructuredData;
